// --- Dependencies -------------------------------------------------------------------------------
import { Link } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import type { TyWarranty, TyWarrantyRecord, TyWarrantyState } from "@/types";
import { formatDate, readLangValue, warrantyDaysLeft, warrantyElapsed, warrantyState } from "@/utils";
import Icon from "@/UI/atoms/Icon";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
export type TyWarrantyDetails = {
    warranty: ({ kind: "template" } & TyWarranty) | ({ kind: "record" } & TyWarrantyRecord);
    /** Records only — following one up is writing down who was called about it. */
    onContact?: () => void;
    /** Templates only, and only while the unit is unsold. */
    onEdit?: () => void;
    onRemove?: () => void;
};

type Props = { onClose: () => void; details?: unknown };

const DAY = 24 * 60 * 60 * 1000;

/**
 * One guarantee, whole.
 *
 * **The cover is drawn, not listed.** Three dates and a duration in a facts grid is four numbers
 * somebody has to do arithmetic on; the same four as a filled rail with today marked on it answers
 * "how much of this is left" before it is read. That bar is the reason this popup exists rather
 * than four more rows on the card.
 *
 * A template has no rail. Its clock has not started — there is nothing to fill — so it shows its
 * duration and says what will start it.
 *
 * **Both halves of the pair are links.** A guarantee is the one record that belongs to a unit and
 * to a person equally, and whichever one you arrived from, the other is the next place you want.
 */
export default function WarrantyPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";
    const { warranty, onContact, onEdit, onRemove } = details as TyWarrantyDetails;

    /** Every action here opens another popup, so this one gets out of the way first. */
    const handle = (action: () => void) => () => {
        onClose();
        action();
    };

    /** The live half, or `null` for a template — which is the half that has dates at all. */
    const record = warranty.kind === "record" ? warranty : null;
    const state: TyWarrantyState = record ? warrantyState(record) : "notStarted";
    const daysLeft = record ? warrantyDaysLeft(record) : null;

    const unit = record && typeof record.unit === "object" && record.unit ? record.unit : null;
    const project = unit && typeof unit.project === "object" && unit.project ? unit.project : null;
    const customer = record && typeof record.customer === "object" && record.customer ? record.customer : null;

    /**
     * How far along the cover is. Read from the dates rather than from `months`, because the dates
     * are what a person was shown and a month is not a fixed length of time.
     */
    const elapsed = record ? warrantyElapsed(record) : null;
    const starts = record?.startsAt ? new Date(record.startsAt).getTime() : null;
    const ends = record?.endsAt ? new Date(record.endsAt).getTime() : null;
    const span = starts !== null && ends !== null && ends > starts ? ends - starts : null;

    return (
        <section className={`warranty-popup warranty-popup--${state}`}>
            <button type="button" className="warranty-popup__close" onClick={onClose} aria-label={t.common.close}>
                <Icon name="close" size={16} />
            </button>

            <header className="warranty-popup__hero">
                <span className="warranty-popup__medallion" aria-hidden="true">
                    <Icon name="crown" size={26} />
                </span>

                <h2 className="warranty-popup__title">{t.properties.warrantyTypes[warranty.type]}</h2>
                <span className="warranty-popup__state">{t.warranties.state[state]}</span>
            </header>

            <div className="warranty-popup__body">
                {/* ─── The cover, drawn ─────────────────────────── */}
                {record && elapsed !== null && span ? (
                    <section className="warranty-popup__cover">
                        <header className="warranty-popup__cover-head">
                            <span className="warranty-popup__cover-title">{t.warranties.timeline}</span>
                            <strong className="warranty-popup__cover-left">
                                {daysLeft !== null && daysLeft > 0
                                    ? t.warranties.daysLeft.replace("{days}", String(daysLeft))
                                    : t.warranties.state.expired}
                            </strong>
                        </header>

                        <div
                            className="warranty-popup__rail"
                            role="progressbar"
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-valuenow={Math.round(elapsed * 100)}
                        >
                            <span className="warranty-popup__rail-fill" style={{ inlineSize: `${Math.round(elapsed * 100)}%` }} />
                        </div>

                        <div className="warranty-popup__rail-ends">
                            <span>
                                <small>{t.warranties.startsAt}</small>
                                <bdi dir="ltr">{formatDate(record.startsAt, lang)}</bdi>
                            </span>
                            <span className="warranty-popup__rail-end">
                                <small>{t.warranties.endsAt}</small>
                                <bdi dir="ltr">{formatDate(record.endsAt, lang)}</bdi>
                            </span>
                        </div>
                    </section>
                ) : (
                    <p className="warranty-popup__note">{t.warranties.stateHint.notStarted}</p>
                )}

                {/* ─── What it is ────────────────────────────────── */}
                <dl className="warranty-popup__facts">
                    <div className="warranty-popup__fact">
                        <dt>{t.warranties.duration}</dt>
                        <dd>
                            {warranty.months} {t.properties.months}
                        </dd>
                    </div>
                    {elapsed !== null && (
                        <div className="warranty-popup__fact">
                            <dt>{t.warranties.elapsed}</dt>
                            <dd>{Math.round(elapsed * 100)}%</dd>
                        </div>
                    )}
                    {span !== null && (
                        <div className="warranty-popup__fact">
                            <dt>{t.warranties.covers}</dt>
                            <dd>
                                {Math.round(span / DAY)} {t.warranties.daysUnit}
                            </dd>
                        </div>
                    )}
                </dl>

                {/* ─── Who stands behind it ──────────────────────── */}
                <section className="warranty-popup__provider">
                    <span className="warranty-popup__provider-text">
                        <small>{t.warranties.provider}</small>
                        <strong>{warranty.provider?.name || t.warranties.noProvider}</strong>
                    </span>

                    {warranty.provider?.phone && (
                        <a className="warranty-popup__call" href={`tel:${warranty.provider.phone}`}>
                            <Icon name="phone" size={14} />
                            <bdi dir="ltr">{warranty.provider.phone}</bdi>
                        </a>
                    )}
                </section>

                {/*
                 * ─── The two it belongs to ──────────────────────
                 *
                 * **One row, two destinations.** They are the same gesture pointed at the two halves
                 * of the same record, so stacking them read as a list of options rather than as the
                 * pair they are — and the word "open" in front of each said nothing the chevron was
                 * not already saying twice.
                 */}
                {(unit || customer) && (
                    <div className="warranty-popup__links">
                        {unit && project && (
                            <Link
                                className="warranty-popup__link"
                                to={`/properties/${project.slug}/units/${encodeURIComponent(unit.number)}`}
                                onClick={onClose}
                            >
                                <Icon name="properties" size={20} />
                                <span>
                                    <small>{t.warranties.unit}</small>
                                    <strong>
                                        {readLangValue(project.name, lang)} · {unit.number}
                                    </strong>
                                </span>
                            </Link>
                        )}

                        {customer && (
                            <Link className="warranty-popup__link" to={`/crm/customers/${customer._id}`} onClick={onClose}>
                                <Icon name="team" size={20} />
                                <span>
                                    <small>{t.warranties.customer}</small>
                                    <strong>{customer.name}</strong>
                                </span>
                            </Link>
                        )}
                    </div>
                )}

                {warranty.notes && <p className="warranty-popup__notes">{warranty.notes}</p>}

                {/*
                 * The actions scroll with everything else rather than being pinned to the bottom.
                 * A pinned bar is for a form, where the save is the point of the panel; this is a
                 * record being read, and a permanent bar over it took a fifth of a phone screen to
                 * offer something nobody opened it for.
                 */}
                {(onContact || onEdit || onRemove) && (
                    <footer className="warranty-popup__actions">
                        {onContact && (
                            <button type="button" className="warranty-popup__action warranty-popup__action--main" onClick={handle(onContact)}>
                                <Icon name="phone" size={14} />
                                {t.warranties.contactAbout}
                            </button>
                        )}
                        {onEdit && (
                            <button type="button" className="warranty-popup__action warranty-popup__action--main" onClick={handle(onEdit)}>
                                <Icon name="edit" size={14} />
                                {t.common.edit}
                            </button>
                        )}
                        {onRemove && (
                            <button type="button" className="warranty-popup__action warranty-popup__action--danger" onClick={handle(onRemove)}>
                                <Icon name="trash" size={14} />
                                {t.common.remove}
                            </button>
                        )}
                    </footer>
                )}
            </div>
        </section>
    );
}
